import React, { useState } from "react";
import { IDragDropOptions, IDropdownOption } from "@fluentui/react";
import { ApprovalLevels } from "../../shared/models/UserAccess.model";

interface EditLevelPanelProps {
  mode: string;
  initialData: Partial<ApprovalLevels>;
  userOptions: IDropdownOption[];
  onClose: () => void;
  onSubmit: (levelData: ApprovalLevels) => void;
  submitError: string;
  approverLevels: ApprovalLevels[];
}

const normalizeKey = (key?: IDropdownOption["key"]): string => {
  if (key === null || key === undefined) {
    return "";
  }
  return key.toString();
};

const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    position: "fixed",
    right: 0,
    top: 0,
    width: "400px",
    height: "100vh",
    background: "#fff",
    zIndex: 1000,
    padding: "32px 24px",
    boxSizing: "border-box",
    boxShadow: "0 0 16px rgba(0,0,0,0.12)",
    overflowY: "auto",
  },
  closeButton: {
    position: "absolute",
    top: "16px",
    right: "16px",
    background: "transparent",
    border: "none",
    fontSize: "18px",
    cursor: "pointer"
  },
  header: {
    marginBottom: "24px",
    fontWeight: 600
  },
  fieldWrapper: {
    marginBottom: "16px"
  },
  label: {
    fontWeight: 500
  },
  input: {
    width: "100%",
    marginTop: "4px",
    padding: "6px",
    border: "1px solid #ccc",
    borderRadius: "4px"
  },
  saveButton: {
    width: "100%",
    padding: "10px",
    background: "#0078d4",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    fontWeight: 600,
    fontSize: "16px",
    cursor: "pointer",
    marginTop: "16px"
  },
  error: {
    color: "red",
    marginTop: "12px",
    fontSize: "14px"
  }
};

const categoryDirectory: { key: string; text: string }[] = [
  { key: "Administrator", text: "Administrator" },
  { key: "Manager", text: "Manager" },
  { key: "Business Owner", text: "Business Owner" },
  { key: "Earnings_Business_Owner", text: "Business Owner" },
  { key: "FTEManager", text: "FTE Manager" },
  { key: "HDC Owners", text: "HDC Owners" },
  { key: "MSSalesApprovers", text: "FTE Manager" },
  { key: "MSSalesOEMAdmins", text: "OEM Access Approver" },
  { key: "Studio Administrator", text: "Studio Administrator" },
  { key: "Support", text: "Support" }
];

// Level values for which the Backup Approver Type and Backup Approvers fields must be hidden.
const BACKUP_HIDDEN_VALUES: string[] = [
  "FTEManager",
  "Manager",
  "MSSalesApprovers",
  "MSSalesOEMAdmins"
];

const isBackupHiddenForValue = (value?: string): boolean => {
  if (!value) {
    return false;
  }
  const normalized = value?.replaceAll("$", "").toLowerCase();
  return BACKUP_HIDDEN_VALUES.some(v => v.toLowerCase() === normalized);
};

// Infer backup approver type for UI display purposes only.
// If the stored backup value contains '$' it represents a dynamic selector token; otherwise it is a static list.
const inferBackupApproverType = (backupApprovers?: string): number =>
  typeof backupApprovers === "string" && backupApprovers.includes("$") ? 2 : 1;

const buildInitialState = (userOptions: IDropdownOption[], data?: Partial<ApprovalLevels> | null): ApprovalLevels => {
  const firstOptionKey = normalizeKey(userOptions[0]?.key);
  const baseValue = data?.value?.replaceAll("$", "") || "";
  const fallbackValue = data?.type === 2 ? (baseValue || firstOptionKey) : data?.value || "";
  const userOptionLookup = (value?: string) => {
    const target = value?.toLowerCase();
    return userOptions.find(opt => opt.text?.toLowerCase() === target || opt.key?.toString().toLowerCase() === target);
  };

  const resolvedCategory = data?.type === 2
    ? categoryDirectory.find(opt => opt.key.toLowerCase() === (userOptionLookup(fallbackValue)?.key?.toString().toLowerCase() || ""))?.text || data?.category || ""
    : data?.category || "";

  const rawBackup = data?.backupApprovers ?? (data as any)?.backUpApprovers ?? "";

  // Strip '$' tokens and resolve to a user option key for the dynamic dropdown's selected value.
  let backupApprovers: string = rawBackup;
  if (inferBackupApproverType(rawBackup) === 2 && backupApprovers) {
    backupApprovers = backupApprovers?.replaceAll("$", "");
    const lookup = userOptionLookup(backupApprovers);
    if (lookup) {
      backupApprovers = normalizeKey(lookup.key);
    }
  }

  return {
    levelId: data?.levelId ?? 0,
    type: data?.type ?? 2,
    category: resolvedCategory,
    value: data?.type === 2 ? (normalizeKey(userOptionLookup(fallbackValue)?.key) || fallbackValue) : fallbackValue,
    backupApprovers,
    externalUsersApproversList: data?.externalUsersApproversList,
    additionalInfo: data?.additionalInfo ?? "",
    externalUsers: data?.externalUsers ?? "",
  };
};

const EditLevelPanel: React.FC<EditLevelPanelProps> = ({
  mode,
  initialData,
  userOptions,
  onClose,
  onSubmit,
  submitError,
  approverLevels
}) => {
  

  const [levelData, setLevelData] = useState<ApprovalLevels>(buildInitialState(userOptions ,initialData));
  // UI-only state. Inferred from the backup approvers value ('$' present => Dynamic, otherwise Static).
  // This is never sent to the backend; only `backupApprovers` is submitted.
  const [backupApproverType, setBackupApproverType] = useState<number>(
    inferBackupApproverType(initialData?.backupApprovers ?? (initialData as any)?.backUpApprovers)
  );
  const [localError, setLocalError] = useState<string>("");

  const handleOnChangeValueSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue: ApprovalLevels = {...levelData}

    const match = categoryDirectory.find(opt => opt.key.toLowerCase() === event.target.value.toLowerCase());
    newValue.category = match ? match.text : "";
    newValue.value = event.target.value;

    setLevelData(newValue);
  }

  const handleOnChangeValueInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue: ApprovalLevels = {...levelData}
    newValue.value = event.target.value;

    setLevelData(newValue);
  }

  const handleOnChangeBackup = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newBackup: ApprovalLevels = {...levelData}
    newBackup.backupApprovers = event.target.value;

    setLevelData(newBackup);
  }

  const handleOnChangeBackupSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newBackup: ApprovalLevels = {...levelData}
    newBackup.backupApprovers = event.target.value;

    setLevelData(newBackup);
  }

  const handleOnChangeBackupType = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedType = Number(event.target.value);
    setBackupApproverType(selectedType);

    const newBackup: ApprovalLevels = {...levelData}
    if (selectedType === 2) {
      // Dynamic: default to the first user option that is not in the hidden list.
      const firstAllowed = userOptions.find(
        opt => !BACKUP_HIDDEN_VALUES.some(v => v.toLowerCase() === normalizeKey(opt.key).toLowerCase())
      );
      newBackup.backupApprovers = normalizeKey(firstAllowed?.key);
    } else {
      // Static: clear, user will enter semicolon-separated aliases
      newBackup.backupApprovers = "";
    }

    setLevelData(newBackup);
  }

  const handleOnChangeType = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newType: ApprovalLevels = {...levelData}
    const selectedType = Number(event.target.value);
    newType.type = selectedType;

    if (selectedType === 1) {
      newType.category = "";
      newType.value = "";
      newType.backupApprovers = "";
      newType.additionalInfo = "";
    }
    else {
      const matchKey = userOptions[0]?.key?.toString() ?? "";
      const match = categoryDirectory.find(opt => opt.key.toLowerCase() === matchKey.toLowerCase());
      newType.category = match ? match.text : "";
      newType.value = matchKey;
      newType.backupApprovers = "";
      newType.additionalInfo = "";
    }

    setBackupApproverType(1);
    setLevelData(newType);
  }

  const handleOnChangeInfo = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newInfo: ApprovalLevels = {...levelData}
    newInfo.additionalInfo = event.target.value;

    setLevelData(newInfo);
  }

  const handleSubmit = () => {
    if (!levelData.value) {
      setLocalError("Value cannot be empty.");
      return;
    }
    if (levelData.type === 1) {
      levelData.value = levelData.value
        .split(";")
        .map((v: string) => v.trim())
        .filter(Boolean)
        .join(";");
      if (!levelData.value) {
        setLocalError("Static approver list cannot be empty.");
        return;
      }
    }
    let normalizedBackup: string;
    if (isBackupHiddenForValue(levelData.value)) {
      normalizedBackup = "";
    } else if (backupApproverType === 2) {
      const rawBackup = (levelData.backupApprovers || "").replaceAll("$", "");
      normalizedBackup = rawBackup ? `$${rawBackup}$` : "";
    } else {
      normalizedBackup = (levelData.backupApprovers || "")
        .split(";")
        .map((v: string) => v.trim())
        .filter(Boolean)
        .join(";");
    }

    if (
      levelData.type === 2 &&
      approverLevels.some(
        (l) =>
          l.levelId !== levelData.levelId &&
          l.value?.toLowerCase()?.replaceAll("$", "") === levelData.value?.toLowerCase()
      )
    ) {
      setLocalError("Duplicate category for multiple levels.");
      return;
    }
    setLocalError("");

    const payload: ApprovalLevels = {
      levelId: levelData.levelId,
      type: levelData.type,
      category: levelData.category || undefined,
      value:
        levelData.type === 2 && !levelData.value.startsWith("$")
          ? `$${levelData.value}$`
          : levelData.value,
      backupApprovers: normalizedBackup || undefined,
      externalUsersApproversList: levelData.externalUsersApproversList,
      additionalInfo: levelData.additionalInfo?.trim() || undefined,
      externalUsers: levelData.externalUsers || undefined,
    };

    onSubmit(payload);
  };

  return (
    <div style={styles.wrapper}>
      <button
        style={styles.closeButton}
        onClick={onClose}
        aria-label="Close"
      >
        ×
      </button>
      <h3 style={styles.header}>
        {mode === "add" ? "Add Level" : "Edit Level"}
      </h3>
      <div style={styles.fieldWrapper}>
        <label style={styles.label}>Level:</label>
        <input
          style={styles.input}
          value={levelData.levelId}
          readOnly
        />
      </div>
      <div style={styles.fieldWrapper}>
        <label style={styles.label}>Level Type:</label>
        <select
          style={styles.input}
          value={levelData.type}
          onChange={handleOnChangeType}
        >
          <option value={2}>Dynamic</option>
          <option value={1}>Static</option>
        </select>
      </div>
      <div style={styles.fieldWrapper}>
        <label style={styles.label}>Value:</label>
        {levelData.type === 2 ? (
          <select
            style={styles.input}
            value={levelData.value?.replaceAll("$", "") || userOptions[0]?.key}
            onChange={handleOnChangeValueSelect}
          >
            {userOptions.map(opt => (
              <option key={opt.key} value={opt.key}>
                {opt.text}
              </option>
            ))}
          </select>
        ) : (
          <input
            style={styles.input}
            value={levelData.value || ""}
            onChange={handleOnChangeValueInput}
            placeholder="Semicolon separated list"
          />
        )}
        <small>
          {levelData.type === 1
            ? "Enter approvers as a semicolon separated list (e.g. user1;user2;user3)"
            : null}
        </small>
      </div>
      {!isBackupHiddenForValue(levelData.value) && (
        <>
          <div style={styles.fieldWrapper}>
            <label style={styles.label}>Backup Approver Type:</label>
            <select
              style={styles.input}
              value={backupApproverType}
              onChange={handleOnChangeBackupType}
            >
              <option value={2}>Dynamic</option>
              <option value={1}>Static</option>
            </select>
          </div>
          <div style={styles.fieldWrapper}>
            <label style={styles.label}>Backup Approvers:</label>
            {backupApproverType === 2 ? (() => {
              const backupOptions = userOptions.filter(
                opt => !BACKUP_HIDDEN_VALUES.some(v => v.toLowerCase() === normalizeKey(opt.key).toLowerCase())
              );
              const currentBackupKey = (levelData.backupApprovers || "").replaceAll("$", "");
              const selectedBackupKey =
                backupOptions.find(opt => normalizeKey(opt.key).toLowerCase() === currentBackupKey.toLowerCase())
                  ? currentBackupKey
                  : normalizeKey(backupOptions[0]?.key);
              return (
                <select
                  style={styles.input}
                  value={selectedBackupKey}
                  onChange={handleOnChangeBackupSelect}
                >
                  {backupOptions.map(opt => (
                    <option key={`backup-${opt.key}`} value={opt.key}>
                      {opt.text}
                    </option>
                  ))}
                </select>
              );
            })() : (
              <input
                style={styles.input}
                value={levelData.backupApprovers || ""}
                onChange={handleOnChangeBackup}
                placeholder="Semicolon separated backup approvers"
              />
            )}
            <small>
              {backupApproverType === 1
                ? "Optional: Backup approver aliases (e.g. user1;user2)"
                : "Select a default dynamic backup approver"}
            </small>
          </div>
        </>
      )}
      <div style={styles.fieldWrapper}>
        <label style={styles.label}>Additional Info:</label>
        <textarea
          style={{ ...styles.input, minHeight: "60px", resize: "vertical" }}
          value={levelData.additionalInfo || ""}
          onChange={handleOnChangeInfo}
          placeholder="Additional information"
        />
      </div>
      <button
        style={styles.saveButton}
        onClick={handleSubmit}
      >
        Save
      </button>
      {(localError || submitError) && (
        <div style={styles.error}>
          {localError || submitError}
        </div>
      )}
    </div>
  );
};

export default EditLevelPanel;
